import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { REFERRAL_SEARCH_PARAMS } from './constants';
import { ReferralContext } from './referral-context';
import type {
  ReferralContextType,
  ReferralData,
  SetReferralData,
} from './types';

enum REFERRAL_STORAGE_KEYS {
  refMagCode = 'registration_referral_mag_code',
  refDeptCode = 'registration_referral_dept_code',
  refDeptId = 'registration_referral_dept_id',
  refBranchId = 'registration_referral_branch_id',
  linkId = 'registration_referral_link_id',
}

interface ReferralProviderProps {
  children: JSX.Element;
}

export const ReferralProvider = ({ children }: ReferralProviderProps) => {
  const [searchParams] = useSearchParams();
  const [isIncorrectLinkError, setIsIncorrectLinkError] = useState(false);
  const [refData, setRefData] = useState<ReferralData>({
    refMagCode: sessionStorage.getItem(REFERRAL_STORAGE_KEYS.refMagCode),
    refDeptCode: sessionStorage.getItem(REFERRAL_STORAGE_KEYS.refDeptCode),
    refDeptId: sessionStorage.getItem(REFERRAL_STORAGE_KEYS.refDeptId),
    refBranchId: sessionStorage.getItem(REFERRAL_STORAGE_KEYS.refBranchId),
    linkId: sessionStorage.getItem(REFERRAL_STORAGE_KEYS.linkId),
  });

  const setReferralData = useCallback<SetReferralData>(
    (referralData) => {
      Object.entries(REFERRAL_STORAGE_KEYS).forEach(([field, storageKey]) => {
        const value = referralData[field as keyof ReferralData];

        if (value) {
          sessionStorage.setItem(storageKey, value);
        } else {
          sessionStorage.removeItem(storageKey);
        }
      });
      setRefData(referralData);
    },
    [setRefData]
  );

  const clearReferralData = useCallback(() => {
    Object.values(REFERRAL_STORAGE_KEYS).forEach((storageKey) => {
      sessionStorage.removeItem(storageKey);
    });
  }, []);

  useEffect(() => {
    const referralSearchParams: ReferralData = {
      refMagCode: searchParams.get(REFERRAL_SEARCH_PARAMS.REF_MAG_CODE),
      refDeptCode: searchParams.get(REFERRAL_SEARCH_PARAMS.REF_DEPT_CODE),
      refDeptId: searchParams.get(REFERRAL_SEARCH_PARAMS.REF_DEPT_ID),
      refBranchId: searchParams.get(REFERRAL_SEARCH_PARAMS.REF_BRANCH_ID),
      linkId: searchParams.get(REFERRAL_SEARCH_PARAMS.GUID),
    };

    setReferralData(referralSearchParams);
  }, [searchParams, setReferralData]);

  const context = useMemo<ReferralContextType>(
    () => ({
      referralData: refData,
      setReferralData,
      clearReferralData,
      isIncorrectLinkError,
      setIsIncorrectLinkError,
    }),
    [refData, setReferralData, clearReferralData, isIncorrectLinkError]
  );

  return (
    <ReferralContext.Provider value={context}>
      {children}
    </ReferralContext.Provider>
  );
};
